import appSettings from "@/appsettings.json";
import SubmissionTokensBrowserModal from "@/components/modals/SubmissionTokensBrowserModal";
import { SessionContext } from "@/context/SessionContext";
import ConductionAPI from "@/javascript/conduction-api";
import { Classroom, ProjectSubmissionToken } from "@/javascript/user-objects";
import { Utils } from "@/javascript/utils";
import {
	Badge,
	Button,
	Card,
	Group,
	Loader,
	NumberInput,
	ScrollArea,
	Table,
	Text,
	TextInput,
	Title,
	Tooltip,
} from "@mantine/core";
import {
	IconAlertCircle,
	IconCheck,
	IconClipboard,
	IconCopy,
	IconEye,
	IconHomePlus,
	IconLink,
	IconSearch,
	IconTrash,
} from "@tabler/icons-react";
import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ClassroomCreatorModal from "./ClassroomCreatorModal";
import ClassroomDeletionModal from "./ClassroomDeletionModal";
import ClassroomDuplcatorModal from "./ClassroomDuplicatorModal";
import ClassroomSongAssignor from "./ClassroomSongAssignor";

const ClassroomsBrowserTable: React.FC = () => {
	const [classrooms, setClassrooms] = useState<Classroom[] | null | undefined>(undefined);
	const [showClassroomCreationModal, setShowClassroomCreationModal] = useState(false);
	const [classroomToEdit, setClassroomToEdit] = useState<Classroom | null>(null);
	const [showSubmissionTokenBrowserModal, setShowSubmissionTokenBrowserModal] = useState(false);
	const [showClassroomDuplicateModal, setShowClassroomDuplicateModal] = useState(false);
	const [showClassroomDeleteModal, setShowClassroomDeleteModal] = useState(false);
	const [showClassroomSongsModal, setShowClassroomSongsModal] = useState(false);

	// NEW: name filter state
	const [nameQuery, setNameQuery] = useState<string>("");

	const session = useContext(SessionContext);
	if (!session) {
		throw new Error("ClassroomsBrowser must be a child of SessionContext.");
	}
	const token = session.token;
	if (!token) return null;

	const navigate = useNavigate();
	const location = useLocation();

	const fetchClassrooms = () => {
		setClassrooms(undefined);

		ConductionAPI.getAllClassrooms(token)
			.then((loadedClassroomsInfo: Classroom[] | null) => {
				if (!loadedClassroomsInfo || loadedClassroomsInfo.length === 0) {
					setClassrooms(null);
				} else {
					setClassrooms(
						loadedClassroomsInfo.sort(
							(a, b) => b.dateCreated.getTime() - a.dateCreated.getTime() // Newest first
						)
					);
					if (classroomToEdit != null) {
						const updatedClassroom = loadedClassroomsInfo.find((c) => c.id === classroomToEdit.id) ?? null;
						setClassroomToEdit(updatedClassroom);
					}
				}
			})
			.catch(() => {
				Utils.displayNotification(
					"Error Fetching Classrooms",
					"An error occurred while retrieving classrooms.",
					"red",
					<IconAlertCircle size={16} />,
					true
				);
				setClassrooms(null);
			});
	};

	useEffect(() => {
		fetchClassrooms();
	}, [token]);

	const filteredClassrooms = useMemo(() => {
		if (!Array.isArray(classrooms)) return classrooms;
		const q = nameQuery.trim().toLowerCase();
		if (!q) return classrooms;
		return classrooms.filter((c) => {
			const name = (c.name ?? "").toLowerCase();
			const desired = (c.desiredName ?? "").toLowerCase();
			return name.includes(q) || desired.includes(q);
		});
	}, [classrooms, nameQuery]);

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text).then(() => {
			Utils.displayNotification("Copied to Clipboard", `"${text}" is ready to paste!`, "green", <IconClipboard size={16} />);
		});
	};

	const handleClassroomAdminLimitChange = (classroomId: string, newLimit: number) => {
		if (newLimit < 0) {
			Utils.displayNotification("Invalid Value", undefined, "blue", <IconAlertCircle />);
			return;
		}

		const notificationId = Utils.displayNotification("Updating classroom admin limit...", undefined, "blue", "loading");

		ConductionAPI.setClassroomAdminLimit(classroomId, newLimit, token)
			.then(() => {
				Utils.updateNotification(
					notificationId,
					"Successfully updated classrooms admin limit",
					undefined,
					"green",
					<IconCheck size={16} />
				);
				fetchClassrooms();
			})
			.catch((e: any) => {
				console.warn(e);
				Utils.updateNotification(notificationId, "An Error Occured", undefined, "red", <IconAlertCircle size={16} />, true);
			});
	};

	const handleStudentLimitChange = (classroomId: string, newLimit: number) => {
		if (newLimit < 0) {
			Utils.displayNotification("Invalid Value", undefined, "blue", <IconAlertCircle />);
			return;
		}

		const notificationId = Utils.displayNotification("Updating student limit...", undefined, "blue", "loading");

		ConductionAPI.setStudentLimit(classroomId, newLimit, token)
			.then(() => {
				Utils.updateNotification(
					notificationId,
					"Successfully updated student limit",
					undefined,
					"green",
					<IconCheck size={16} />
				);
				fetchClassrooms();
			})
			.catch((e: any) => {
				console.warn(e);
				Utils.updateNotification(notificationId, "An Error Occured", undefined, "red", <IconAlertCircle size={16} />, true);
			});
	};

	const handleRename = (classroomId: string, newName: string) => {
		if (newName.trim().length <= 0) {
			Utils.displayNotification("Invalid Name", undefined, "blue", <IconAlertCircle />);
			return;
		}

		const notificationId = Utils.displayNotification("Renaming classroom...", undefined, "blue", "loading");

		ConductionAPI.renameClassroom(classroomId, newName, token)
			.then(() => {
				Utils.updateNotification(notificationId, "Successfully renamed classroom", undefined, "green", <IconCheck size={16} />);
				fetchClassrooms();
			})
			.catch((e: any) => {
				console.warn(e);
				Utils.updateNotification(notificationId, "An Error Occured", undefined, "red", <IconAlertCircle size={16} />, true);
			});
	};

	return (
		<Card h="80vh" w="90%">
			{/* Modals */}
			<ClassroomCreatorModal
				showModal={showClassroomCreationModal}
				setShowModal={setShowClassroomCreationModal}
				onCreate={fetchClassrooms}
			/>
			<SubmissionTokensBrowserModal
				classroom={classroomToEdit}
				showModal={showSubmissionTokenBrowserModal}
				setShowModal={(show) => {
					if (!show) {
						setClassroomToEdit(null);
						setShowSubmissionTokenBrowserModal(false);
					}
				}}
				onChange={fetchClassrooms}
			/>
			<ClassroomSongAssignor
				classroom={classroomToEdit}
				showModal={showClassroomSongsModal}
				setShowModal={(show) => {
					if (!show) {
						setClassroomToEdit(null);
						setShowClassroomSongsModal(false);
					}
				}}
				onDuplication={fetchClassrooms}
			/>
			<ClassroomDuplcatorModal
				classroom={classroomToEdit}
				showModal={showClassroomDuplicateModal}
				setShowModal={(show) => {
					if (!show) {
						setClassroomToEdit(null);
						setShowClassroomDuplicateModal(false);
					}
				}}
				onDuplication={fetchClassrooms}
			/>
			<ClassroomDeletionModal
				classroom={classroomToEdit}
				showModal={showClassroomDeleteModal}
				setShowModal={(show) => {
					if (!show) {
						setClassroomToEdit(null);
						setShowClassroomDeleteModal(false);
					}
				}}
				onDeletion={fetchClassrooms}
			/>

			<Group justify="space-between" mb="md">
				<Title>Classrooms</Title>
				<Button onClick={() => setShowClassroomCreationModal(true)} leftSection={<IconHomePlus size={16} />}>
					Create New Classroom
				</Button>
			</Group>

			<ScrollArea style={{ width: "100%", minHeight: "100%" }}>
				{classrooms === undefined ? (
					<Loader height="100%" width="100%" radius="md" />
				) : classrooms === null ? (
					<Text>No classrooms found.</Text>
				) : (
					<Table striped stickyHeader highlightOnHover withColumnBorders>
						<Table.Thead>
							<Table.Tr>
								<Table.Th>
									<div style={{ display: "grid", gap: 6, minWidth: 260 }}>
										<Text size="sm" fw={600}>
											Name
										</Text>
										<TextInput
											size="xs"
											placeholder="Search name..."
											value={nameQuery}
											onChange={(e) => setNameQuery(e.currentTarget.value)}
											leftSection={<IconSearch size={14} />}
											leftSectionPointerEvents="none"
											autoComplete="off"
										/>
									</div>
								</Table.Th>
								<Table.Th>Created</Table.Th>
								<Table.Th>Classroom Admins</Table.Th>
								<Table.Th>Students</Table.Th>
								<Table.Th>Projects</Table.Th>
								<Table.Th>Credits</Table.Th>
								<Table.Th>Songs in Library</Table.Th>
								<Table.Th>Instructor Code</Table.Th>
								<Table.Th>Actions</Table.Th>
							</Table.Tr>
						</Table.Thead>
						<Table.Tbody>
							{filteredClassrooms!.map((classroom) => {
								const usedActiveCredits =
									classroom.tokens?.reduce(
										(acc: number, token: ProjectSubmissionToken) => acc + (token.isActive() ? token.submissions.length : 0),
										0
									) ?? 0;
								const totalActiveCredits =
									classroom.tokens?.reduce((acc: number, token: ProjectSubmissionToken) => acc + token.getActiveCredits(), 0) ??
									0;
								return (
									<Table.Tr key={classroom.name}>
										<Table.Td>
											<Group>
												<TextInput
													value={classroom.desiredName}
													maxLength={30}
													onChange={(e) => {
														const newClassrooms = classrooms.map((c) => {
															if (c.id != classroom.id) return c;
															return classroom.setDesiredName(e.target.value);
														});
														setClassrooms(newClassrooms);
													}}
												/>
												<Button
													size={"xs"}
													disabled={classroom.name === classroom.desiredName}
													onClick={() => handleRename(classroom.id, classroom.desiredName)}
												>
													<IconCheck size={16} />
												</Button>
											</Group>
										</Table.Td>
										<Table.Td>
											<Badge color="blue">{formatDateString(classroom.dateCreated)}</Badge>
										</Table.Td>
										<Table.Td>
											<Group>
												<Text>{`${classroom.getClassroomAdmins()?.length ?? 0} / `}</Text>
												<Tooltip label={`Edit classroom admin limit for the ${classroom.name} classroom`} withArrow>
													<NumberInput
														style={{
															width: 70,
														}}
														value={classroom.desiredClassroomAdminLimit}
														min={0}
														max={50}
														onChange={(value) => {
															if (typeof value === "string") return;
															const newClassrooms = classrooms.map((c) => {
																if (c.id != classroom.id) return c;
																return classroom.setDesiredClassroomAdminLimit(value);
															});
															setClassrooms(newClassrooms);
														}}
													/>
												</Tooltip>
												<Button
													size={"xs"}
													disabled={classroom.classroomAdminLimit === classroom.desiredClassroomAdminLimit}
													onClick={() => handleClassroomAdminLimitChange(classroom.id, classroom.desiredClassroomAdminLimit)}
												>
													<IconCheck size={16} />
												</Button>
											</Group>
										</Table.Td>
										<Table.Td>
											<Group>
												<Text>{`${classroom.getClassroomStudents()?.length ?? 0} / `}</Text>
												<Tooltip label={`Edit student limit for the ${classroom.name} classroom`} withArrow>
													<NumberInput
														style={{
															width: 70,
														}}
														value={classroom.desiredStudentLimit}
														min={0}
														max={500}
														onChange={(value) => {
															if (typeof value === "string") return;
															const newClassrooms = classrooms.map((c) => {
																if (c.id != classroom.id) return c;
																return classroom.setDesiredStudentLimit(value);
															});
															setClassrooms(newClassrooms);
														}}
													/>
												</Tooltip>
												<Button
													size={"xs"}
													disabled={classroom.studentLimit === classroom.desiredStudentLimit}
													onClick={() => handleStudentLimitChange(classroom.id, classroom.desiredStudentLimit)}
												>
													<IconCheck size={16} />
												</Button>
											</Group>
										</Table.Td>
										<Table.Td>
											<Text>{classroom.projectCount}</Text>
										</Table.Td>
										<Table.Td>
											<Group>
												<Text>{`${usedActiveCredits} / `}</Text>
												<Tooltip label={`Edit submission credit tokens for the ${classroom.name} classroom`} withArrow>
													<Button
														variant="subtle"
														size="xs"
														onClick={() => { setClassroomToEdit(classroom); setShowSubmissionTokenBrowserModal(true); }}
													>
														{totalActiveCredits}
													</Button>
												</Tooltip>
											</Group>
										</Table.Td>
										<Table.Td>
											<Tooltip label={`Edit song library for the ${classroom.name} classroom`} withArrow>
												<Button variant="subtle" size="xs" onClick={() => { setClassroomToEdit(classroom); setShowClassroomSongsModal(true); }}>
													{classroom.defaultSongIds.length}
												</Button>
											</Tooltip>
										</Table.Td>
										<Table.Td>
											<Group>
												<Tooltip label={`Classroom Admin code for the ${classroom.name} classroom`} withArrow>
													<Button
														variant="light"
														size="xs"
														leftSection={<IconCopy size={14} />}
														onClick={() =>
															classroom.classroomAdminAccessCode && copyToClipboard(classroom.classroomAdminAccessCode)
														}
													>
														{classroom.classroomAdminAccessCode}
													</Button>
												</Tooltip>
												<Tooltip label={`Classroom Admin shortcut for the ${classroom.name} classroom`} withArrow>
													<Button
														variant="light"
														size="compact-md"
														onClick={() =>
															classroom.classroomAdminAccessCode &&
															copyToClipboard(`${appSettings.AppBaseUrl}/register/${classroom.classroomAdminAccessCode}`)
														}
													>
														<IconLink size={16} />
													</Button>
												</Tooltip>
											</Group>
										</Table.Td>
										<Table.Td>
											<Group>
												<Tooltip label="View Classroom" withArrow>
													<Button
														size="xs"
														onClick={() =>
															navigate({
																pathname: `/classroom/${classroom.id}`,
																search: location.search,
															})
														}
													>
														<IconEye size={16} />
													</Button>
												</Tooltip>
												<Tooltip label="Duplicate Classroom" withArrow>
													<Button size="xs" onClick={() => { setClassroomToEdit(classroom); setShowClassroomDuplicateModal(true); }}>
														<IconCopy size={16} />
													</Button>
												</Tooltip>
												<Tooltip label={"Delete"} withArrow>
													<Button size="xs" onClick={() => { setClassroomToEdit(classroom); setShowClassroomDeleteModal(true); }}>
														<IconTrash size={20} />
													</Button>
												</Tooltip>
											</Group>
										</Table.Td>
									</Table.Tr>
								);
							})}
						</Table.Tbody>
					</Table>
				)}
			</ScrollArea>
		</Card>
	);
};

function formatDateString(stringDate: Date): string {
	if (!stringDate) return "Invalid Date";
	const date = new Date((stringDate as unknown as string) + " UTC");
	return date.toLocaleDateString();
}

export default ClassroomsBrowserTable;
